// Onboarding helpers — goal chooser, profile seeding, scope keys.
// Centralised here so SplashPage and App.js share one source of truth.

export const GOAL_OPTIONS = [
  {
    key: 'firstTri',
    label: 'My first triathlon',
    sublabel: 'Sprint or Olympic distance',
    icon: '🏊',
  },
  {
    key: 'runningRace',
    label: 'Running',
    sublabel: 'Race prep or general fitness',
    icon: '🏃',
  },
  {
    key: 'improving',
    label: 'Getting faster',
    sublabel: 'Already training — want to improve',
    icon: '⚡',
  },
  {
    key: 'exploring',
    label: 'Just exploring',
    sublabel: 'Browse first, decide later',
    icon: '🧭',
  },
];

const PENDING_KEY = 'triHarderPendingOnboarding';
const PENDING_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export const writePendingOnboarding = (goal, source) => {
  if (!goal) return;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({
      goal,
      source: source || 'unknown',
      createdAt: Date.now(),
    }));
  } catch {}
};

export const readPendingOnboarding = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.goal || !parsed?.createdAt) return null;
    if (Date.now() - parsed.createdAt > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearPendingOnboarding = () => {
  try { localStorage.removeItem(PENDING_KEY); } catch {}
};

// Mirrors the existing keying logic in App.js — Strava users are scoped by
// athlete id; everyone else (intervals OAuth, API key) shares 'triHarderProfile'.
// Demo mode never reads/writes a profile from localStorage.
export const getProfileStorageKey = (creds) => {
  if (creds?.stravaAthleteId) return `triHarderProfile_${creds.stravaAthleteId}`;
  return 'triHarderProfile';
};

// A unique scope identifier for per-user flags (welcome shown, goal seeded).
// Returns null when no auth + not demo (caller should wait).
export const getUserScope = (creds, isDemoMode) => {
  if (isDemoMode) return 'demo';
  if (creds?.stravaAthleteId) return `strava_${creds.stravaAthleteId}`;
  if (creds?.intervalsAthleteId) return `intervals_${creds.intervalsAthleteId}`;
  if (creds?.athleteId) return `apikey_${creds.athleteId}`;
  return null;
};

// Fill-empty-only merge — never overwrites existing values.
const fillEmpty = (section, patch) => {
  const out = { ...section };
  for (const [k, v] of Object.entries(patch)) {
    const current = out[k];
    const isEmpty = current === '' || current === null || current === undefined ||
                    (Array.isArray(current) && current.length === 0);
    if (isEmpty) out[k] = v;
  }
  return out;
};

export const applyGoalSeed = (profile, goal) => {
  if (!profile || !goal) return profile;
  const next = {
    ...profile,
    background: { ...(profile.background || {}) },
    goals: { ...(profile.goals || {}) },
  };

  if (goal === 'firstTri') {
    next.background = fillEmpty(next.background, {
      sportFocus: 'triathlon',
      experienceLevel: 'beginner',
    });
    next.goals = fillEmpty(next.goals, {
      seasonGoals: 'Preparing for an upcoming triathlon',
    });
  } else if (goal === 'runningRace') {
    next.background = fillEmpty(next.background, {
      sportFocus: 'run',
    });
    next.goals = fillEmpty(next.goals, {
      seasonGoals: 'Improving my running',
    });
  }
  // 'improving' and 'exploring' intentionally seed nothing.

  return next;
};

export const goalSeededFlagKey = (scope) => `triHarderGoalSeeded_${scope}`;
export const welcomeShownFlagKey = (scope) => `triHarderWelcomeShown_${scope}`;
export const quickStartDismissedFlagKey = (scope) => `triHarderQuickStartDismissed_${scope}`;
